import { useState, useEffect } from 'react'

/**
 * Name-based responsive hooks. Breakpoints are defined once in the @theme block
 * of src/styles/tailwind.css; Tailwind v4 emits each as a `--breakpoint-<name>`
 * CSS variable on :root, which we read here so JS and class names share one
 * source of truth (no duplicated pixel constants).
 *
 *   const isDesktop = useMin('nav')   // true at/above --breakpoint-nav
 *   const isNarrow  = useMax('nav')   // true below it (mirrors `max-nav:`)
 */

// Cached per name — the value is static, so we read it once and keep the
// resulting query string stable across renders (so useEffect doesn't re-subscribe).
const cache = {}
const readBreakpoint = (name) => {
    if (cache[name] == null || Number.isNaN(cache[name])) {
        const root = getComputedStyle(document.documentElement)
        cache[name] = parseFloat(root.getPropertyValue(`--breakpoint-${name}`))
    }
    return cache[name]
}

// Client-only SPA → lazy-init from matchMedia so the value is correct on the
// first render (no flash, no "default to desktop" workaround).
function useMediaMatch(query) {
    const [matches, setMatches] = useState(() => window.matchMedia(query).matches)

    useEffect(() => {
        const mql = window.matchMedia(query)
        const onChange = () => setMatches(mql.matches)
        onChange() // resync in case `query` changed between renders
        mql.addEventListener('change', onChange)
        return () => mql.removeEventListener('change', onChange)
    }, [query])

    return matches
}

/** True while an arbitrary media query matches (for queries that are not app breakpoints). */
export const useMediaQuery = (query) => useMediaMatch(query)

/** True at or above the named breakpoint (mirrors the `name:` variant). */
export const useMin = (name) => useMediaMatch(`(min-width: ${readBreakpoint(name)}px)`)

/** True below the named breakpoint (mirrors the `max-name:` variant, i.e. width < name). */
export const useMax = (name) => useMediaMatch(`(max-width: ${readBreakpoint(name) - 0.02}px)`)
